import GlobalTuitionDueDate from "~/components/GlobalTuitionDueDate";
import { Form } from "@unform/web";
import {
  Building,
  CalendarSync,
  CircleX,
  HandCoins,
  Mail,
  MessageSquare,
  PlayCircle,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useField } from "@unform/core";
import { addDays, format, isAfter, parseISO } from "date-fns";
import NewDatepicker from "~/pages/Financial/Outbounds/Payees/ModalFilters/NewDatepicker";
import Input from "~/components/RegisterForm/Input";

import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import GlobalAutopay from "./GlobalAutopay";
import { getPriceLists } from "~/functions";
import PricesSimulation from "~/components/PricesSimulation";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { openPaymentModal } from "~/functions/emergepayfn";
import { AlertContext } from "~/App";
import { FullGridContext } from "../..";
import Comments from "./Comments";
import CancelGlobalModal from "./CancelGlobalModal";

export const InputContext = createContext({});

function SessionDateField({
  name,
  title,
  required = false,
  grow = false,
  defaultValue = "",
  InputContext = null,
  onChange = null,
}) {
  const { fieldName, registerField } = useField(name);
  const { setSuccessfullyUpdated } = useContext(InputContext);
  const [value, setValue] = useState(defaultValue || "");
  const fieldRef = useRef({ value: defaultValue || "" });

  useEffect(() => {
    const initialValue = defaultValue || "";
    fieldRef.current.value = initialValue;
    setValue(initialValue);
  }, [defaultValue]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: fieldRef.current,
      path: "value",
      setValue: (ref, nextValue) => {
        const resolved = nextValue || "";
        ref.value = resolved;
        setValue(resolved);
      },
      clearValue: (ref) => {
        ref.value = "";
        setValue("");
      },
    });
  }, [fieldName, registerField]);

  function handleChange(nextValue) {
    fieldRef.current.value = nextValue || "";
    setValue(nextValue || "");
    setSuccessfullyUpdated(false);
    if (onChange) {
      onChange(nextValue || "");
    }
  }

  return (
    <div
      className={`flex flex-col justify-center items-start ${grow ? "grow" : ""}`}
    >
      <div className="px-2 text-xs flex flex-row justify-between items-center">
        {title} {required && <span className="text-red-600">*</span>}
      </div>
      <div className="w-full border border-gray-300 rounded-xs px-2 py-1 text-sm">
        <NewDatepicker
          value={value}
          onChange={handleChange}
          className="w-full"
          placeholder="MM/DD/YYYY"
        />
      </div>
    </div>
  );
}

const invoiceDiscount = (invoice) => parseFloat(invoice.discount || 0);

const invoiceTotal = (invoice) =>
  invoice.total !== null && invoice.total !== undefined
    ? parseFloat(invoice.total)
    : parseFloat(invoice.amount || 0) - invoiceDiscount(invoice);

export default function PagePreview({ access, id, defaultFormType = "preview" }) {
  const { handleOpened, successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const { alertBox } = useContext(AlertContext);
  const [editingPaymentType, setEditingPaymentType] = useState(false);
  const [newPaymentType, setNewPaymentType] = useState("full");
  const [savingPaymentType, setSavingPaymentType] = useState(false);
  const [pageData, setPageData] = useState({
    loaded: false,
    filial: { name: "" },
    student: {},
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("invoices");
  const [globalSession, setGlobalSession] = useState(null);
  const [globalPaymentMethod, setGlobalPaymentMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pricingMode, setPricingMode] = useState("fixed");
  const [paymentType, setPaymentType] = useState("full");
  const [planScheduleOptions, setPlanScheduleOptions] = useState([]);
  const [planScheduleId, setPlanScheduleId] = useState(null);
  const [sessionStartDate, setSessionStartDate] = useState(null);
  const [firstTuitionDueDate, setFirstTuitionDueDate] = useState("");
  const [sessionEndDate, setSessionEndDate] = useState(null);
  const [firstDueDate, setFirstDueDate] = useState(null);
  const [hourlyPreview, setHourlyPreview] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [simulatedTotals, setSimulatedTotals] = useState(null);
  const [priceLists, setPriceLists] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [setAsCurrent, setSetAsCurrent] = useState(false);
  const [activating, setActivating] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [terminationDate, setTerminationDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [terminationMemo, setTerminationMemo] = useState("");

  const student = pageData?.student ?? {};
  const canEditGlobal = access?.MenuHierarchyXGroup?.edit ?? access?.edit;
  const generalForm = useRef();
  const renewForm = useRef();
  const renewingRef = useRef(false);

  const renewSimulationStudent = useMemo(
    () => ({
      ...(pageData.fullStudent ?? {}),
      searchFields: {
        filial_id: pageData.filial_id ?? null,
        processtype_id: pageData.processtype_id ?? null,
        processsubstatus_id: pageData.processsubstatus_id ?? null,
      },
    }),
    [
      pageData.fullStudent,
      pageData.filial_id,
      pageData.processtype_id,
      pageData.processsubstatus_id,
    ]
  );

  const displayedSession =
    sessions.find((s) => s.id === selectedSessionId) ?? globalSession;

  const renewableSession =
    student.status === "Inactive" || student.category === "Ex-student"
      ? null
      : sessions.find((s) => s.status === "active") ?? null;

  const pendingSession = sessions.find((s) => s.status === "pending");

  function formatSessionLabel(session) {
    const period = `${session.session_start_date} → ${session.session_end_date}`;
    return session.sequence ? `#${session.sequence} — ${period}` : period;
  }

  function handleCloseForm() {
    handleOpened(null);
  }

  async function getPageData() {
    try {
      const { data } = await api.get(`/contractsessions/${id}`);
      const student = data.contract?.student ?? {};
      const pmData = await api.get(`/paymentmethods`);
      setPaymentMethods(
        (pmData.data.rows || [])
          .filter((p) => p.is_available_at_enrollment)
          .map((p) => ({ value: p.id, label: p.description.slice(0, 20) }))
      );
      let fullStudent = student;
      if (student?.id) {
        try {
          const { data: studentData } = await api.get(`/prospects/${student.id}`);
          fullStudent = { ...student, ...studentData };
        } catch (err) {
          console.log("Error loading full student:", err);
        }
      }

      const sessionList = data.sessions ?? data.global_session?.sessions ?? [];
      setSessions(sessionList);
      const currentSession =
        sessionList.find((s) => s.is_current) ??
        sessionList.find((s) => s.id === id) ??
        sessionList[sessionList.length - 1] ??
        null;
      setSelectedSessionId(currentSession?.id ?? id);

      setGlobalSession(data.global_session ?? null);
      setPageData({
        fullStudent,
        filial:
          data.filial ??
          fullStudent?.filial ??
          data.contract?.student?.filial ??
          data.contract?.filial ??
          {},
        filial_id: fullStudent?.filial_id ?? data.contract?.filial_id ?? null,
        processsubstatus_id:
          fullStudent?.processsubstatus_id ??
          data.contract?.processsubstatus_id ??
          null,
        processtype_id:
          fullStudent?.processtype_id ?? data.contract?.processtype_id ?? null,
        issuer: student.issuer ?? null,
        issuer_id: student.issuer?.id ?? null,
        loaded: true,
        student,
      });
    } catch (err) {
      console.log(err);
      toast(err?.response?.data?.error, { type: "error", autoClose: 3000 });
    }
  }

  const displayedTuitions = (displayedSession?.receivables ?? []).filter(
    (r) => r.type_detail === "Tuition fee"
  );
  const canChangePaymentType =
    canEditGlobal &&
    displayedSession?.contract_id &&
    student.status !== "Inactive" &&
    student.category !== "Ex-student" &&
    displayedSession?.pricing_mode !== "hourly" &&
    ["active", "pending"].includes(displayedSession?.status) &&
    displayedTuitions.length > 0 &&
    displayedTuitions.every(
      (r) =>
        r.status === "Pending" && Number(r.balance) === Number(r.total)
    );

  function startChangePaymentType() {
    setNewPaymentType(displayedSession?.payment_type ?? "full");
    setEditingPaymentType(true);
  }

  async function submitPaymentTypeChange() {
    setSavingPaymentType(true);
    try {
      await api.put(`/contracts/${displayedSession.contract_id}/session`, {
        contractsession_id: displayedSession.id,
        session_start_date: displayedSession.session_start_date,
        session_end_date: displayedSession.session_end_date,
        first_tuition_due_date: displayedSession.first_tuition_due_date || null,
        plan_schedule: displayedSession.plan_schedule || null,
        plan_schedule_id: null,
        pricing_mode: "fixed",
        payment_type: newPaymentType,
        tuition_amount: Number(displayedSession.tuition_amount) || 0,
        tuition_discount: Number(displayedSession.tuition_discount) || 0,
      });
      toast.success("Payment type updated and tuition invoices regenerated!");
      setEditingPaymentType(false);
      setSuccessfullyUpdated(true);
      await getPageData();
    } catch (err) {
      toast.error(
        err?.response?.data?.error ?? "Could not update the payment type."
      );
    } finally {
      setSavingPaymentType(false);
    }
  }

  function handleChangePaymentType() {
    if (newPaymentType === displayedSession?.payment_type) {
      setEditingPaymentType(false);
      return;
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Saving will cancel the current tuition invoices and generate new ones with the selected payment type. The registration fee is not changed. Continue?",
      buttons: [
        { title: "No", class: "cancel", onPress: () => null },
        { title: "Yes", onPress: () => submitPaymentTypeChange() },
      ],
    });
  }

  async function handleGlobalSendInvoice(receivable) {
    if (!globalPaymentMethod) {
      toast.error("Please select a Payment Method.");
      return;
    }
    try {
      await api.post(`/prospect_payments/payment_link`, {
        issuer_id: pageData.issuer_id,
        registrationFee_id:
          receivable.type_detail === "Registration fee" ? receivable.id : null,
        tuitionFee_id:
          receivable.type_detail === "Tuition fee" ? receivable.id : null,
        paymentmethod_id: globalPaymentMethod,
        enrollment_id: student?.enrollments?.[0]?.id,
        student_id: student?.id,
      });
      toast.success("Invoice link sent to student!");
    } catch (err) {
      toast.error("Failed to send invoice link.");
    }
  }

  async function handleGlobalProcessPayment(receivable) {
    try {
      await openPaymentModal({
        receivable: {
          id: receivable.id,
          total: invoiceTotal(receivable),
          memo: receivable.type_detail,
        },
      });
      const markPaid = (list = []) =>
        list.map((r) => (r.id === receivable.id ? { ...r, status: "Paid" } : r));

      setSessions((prev) =>
        prev.map((session) =>
          session.id === selectedSessionId
            ? { ...session, receivables: markPaid(session.receivables) }
            : session
        )
      );
      setGlobalSession((prev) =>
        prev ? { ...prev, receivables: markPaid(prev.receivables) } : prev
      );
    } catch (err) {
      toast.error("Failed to open payment modal.");
    }
  }

  useEffect(() => {
    async function updatePriceLists() {
      const { priceLists } = await getPriceLists({
        filial_id: pageData.filial_id,
        processsubstatus_id: pageData.processsubstatus_id,
      });
      setPriceLists(priceLists ?? null);
    }
    if (pageData.filial_id && pageData.processsubstatus_id) {
      updatePriceLists();
    }
  }, [pageData.filial_id, pageData.processsubstatus_id]);

  useEffect(() => {
    async function loadPlanSchedules() {
      const filialId = pageData.filial_id;
      const subStatusId = pageData.processsubstatus_id;
      if (!filialId || !subStatusId) {
        setPlanScheduleOptions([]);
        return;
      }
      try {
        const { data } = await api.get(
          `/filials/${filialId}/plan-schedules/${subStatusId}`
        );
        const weekdays = [
          ["monday", "Mon"],
          ["tuesday", "Tue"],
          ["wednesday", "Wed"],
          ["thursday", "Thu"],
          ["friday", "Fri"],
          ["saturday", "Sat"],
          ["sunday", "Sun"],
        ];
        const formatTime = (time) =>
          typeof time === "string" ? time.slice(0, 5) : time;
        const options = (data || [])
          .filter((schedule) => schedule.active !== false)
          .map((schedule) => {
            const activeDays = weekdays
              .filter(([key]) => schedule[key])
              .map(([, label]) => label)
              .join(" - ");
            const timeRange =
              schedule.start_time && schedule.end_time
                ? `${formatTime(schedule.start_time)} to ${formatTime(
                    schedule.end_time
                  )}`
                : "";
            const description = [schedule.period, activeDays, timeRange]
              .filter(Boolean)
              .join(" - ");
            return { value: schedule.id, label: description || schedule.id };
          });
        setPlanScheduleOptions(options);
      } catch (err) {
        console.log("Error fetching plan schedules:", err);
        setPlanScheduleOptions([]);
      }
    }
    loadPlanSchedules();
  }, [pageData.filial_id, pageData.processsubstatus_id]);

  useEffect(() => {
    const filialId = pageData.filial_id;
    const subStatusId = pageData.processsubstatus_id;
    if (
      pricingMode !== "hourly" ||
      !filialId ||
      !subStatusId ||
      !planScheduleId ||
      !sessionStartDate ||
      !sessionEndDate
    ) {
      setHourlyPreview(null);
      return;
    }

    let cancelled = false;
    async function loadPreview() {
      try {
        const { data } = await api.post(`/contracts/preview-tuition`, {
          filial_id: filialId,
          processsubstatus_id: subStatusId,
          plan_schedule_id: planScheduleId,
          session_start_date: sessionStartDate,
          session_end_date: sessionEndDate,
        });
        if (!cancelled) {
          setHourlyPreview(data);
        }
      } catch (err) {
        if (!cancelled) {
          setHourlyPreview(null);
        }
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [
    pageData.filial_id,
    pageData.processsubstatus_id,
    pricingMode,
    planScheduleId,
    sessionStartDate,
    sessionEndDate,
  ]);

  async function handleRenewSubmit(data) {
    if (renewingRef.current) return;
    if (
      !data.session_start_date ||
      !data.session_end_date ||
      !data.first_due_date
    ) {
      toast.error(
        "Session Start Date, Session End Date and Next Invoice Due Date are required."
      );
      return;
    }
    const dueDate = parseISO(data.first_due_date);
    const endDate = parseISO(data.session_end_date);
    if (isAfter(dueDate, endDate)) {
      toast.error("The next invoice due date cannot be after the session end date.");
      return;
    }
    if (
      pricingMode === "fixed" &&
      paymentType === "installments" &&
      isAfter(addDays(dueDate, 60), endDate)
    ) {
      toast.error(
        "The third installment due date cannot be after the session end date."
      );
      return;
    }
    if (pricingMode === "hourly" && !planScheduleId) {
      toast.error("Plan Schedule is required for hourly billing.");
      return;
    }
    if (!renewableSession) {
      toast.error("No active session available to renew.");
      return;
    }
    renewingRef.current = true;
    setRenewing(true);
    try {
      const tuitionAmount =
        pricingMode === "fixed" ? priceLists?.tuition ?? 0 : 0;
      const tuitionDiscount =
        pricingMode === "fixed" ? simulatedTotals?.totalDiscount ?? 0 : 0;

      await api.post(`/contractsessions/${renewableSession.id}/renew`, {
        session_start_date: data.session_start_date,
        session_end_date: data.session_end_date,
        first_tuition_due_date: data.first_tuition_due_date || firstTuitionDueDate || null,
        pricing_mode: pricingMode,
        payment_type: pricingMode === "hourly" ? "full" : paymentType,
        tuition_amount: tuitionAmount,
        tuition_discount: tuitionDiscount,
        plan_schedule:
          planScheduleOptions.find((option) => option.value === planScheduleId)
            ?.label ?? null,
        plan_schedule_id: planScheduleId,
        set_as_current: setAsCurrent,
      });

      toast.success(
        setAsCurrent ? "Session renewed and activated!" : "Session scheduled!"
      );
      setSuccessfullyUpdated(true);
      await getPageData();
    } catch (err) {
      const message = err?.response?.data?.error ?? "Failed to renew session.";
      toast.error(message);
    } finally {
      renewingRef.current = false;
      setRenewing(false);
    }
  }

  async function handleActivateSession(session) {
    setActivating(true);
    try {
      await api.post(`/contractsessions/${session.id}/activate`);
      toast.success("Session activated!");
      setSuccessfullyUpdated(true);
      await getPageData();
    } catch (err) {
      const message =
        err?.response?.data?.error ?? "Failed to activate session.";
      toast.error(message);
    } finally {
      setActivating(false);
    }
  }

  function canCancelGlobalSession() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(terminationDate) || !terminationMemo.trim()) {
      toast.error("Cancellation date and memo are required.");
      return false;
    }
    const futureSessions = sessions.filter((s) => s.status === "pending");
    const futureInvoices = futureSessions.flatMap((s) => s.receivables ?? []);
    if (futureInvoices.some((invoice) => invoice.status === "Paid")) {
      toast.error("A future session has paid invoices. Review them before canceling.");
      return false;
    }
    return true;
  }

  function openCancelModal() {
    if (terminating || !renewableSession || !canCancelGlobalSession()) return;
    setCancelModalOpen(true);
  }

  async function handleTerminateSession() {
    if (terminating || !renewableSession || !canCancelGlobalSession()) return;
    setTerminating(true);
    try {
      await api.post(`/contractsessions/${renewableSession.id}/terminate`, {
        date: terminationDate,
        memo: terminationMemo.trim(),
      });
      setCancelModalOpen(false);
      toast.success("Financial cancellation saved. Inactivate the student in Students next.");
      setSuccessfullyUpdated(true);
      setActiveMenu("invoices");
      await getPageData();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? "Failed to cancel the Global session.");
    } finally {
      setTerminating(false);
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      <div className="flex h-full flex-row items-start justify-between gap-4">
        <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="general"
          >
            <Building size={16} /> General
          </RegisterFormMenu>

          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="invoices"
          >
            <HandCoins size={16} /> Invoices
          </RegisterFormMenu>

          {student.status !== "Inactive" && student.category !== "Ex-student" && (
            <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="renew">
              <CalendarSync size={16} /> Renew Session
            </RegisterFormMenu>
          )}

          {canEditGlobal && student.status !== "Inactive" && student.category !== "Ex-student" && (
            <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="terminate">
              <CircleX size={16} /> Cancel Global
            </RegisterFormMenu>
          )}

          <RegisterFormMenu
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            name="comments"
          >
            <MessageSquare size={16} /> Enrollment Comments
          </RegisterFormMenu>
        </div>
        <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
          <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
            <Form
              ref={generalForm}
              onSubmit={() => null}
              className={`w-full ${activeMenu === "renew" ? "" : "pb-32"}`}
            >
              <InputContext.Provider
                value={{
                  id,
                  generalForm,
                  setSuccessfullyUpdated,
                  fullscreen,
                  setFullscreen,
                  successfullyUpdated,
                  handleCloseForm,
                }}
              >
                {pageData.loaded ? (
                  <>
                    <FormHeader
                      access={access}
                      title={student?.name}
                      InputContext={InputContext}
                      disableSave={true}
                    />

                    <InputLineGroup
                      title="GENERAL"
                      activeMenu={activeMenu === "general"}
                    >
                      <InputLine title="Filial">
                        <Input
                          type="text"
                          name="filial_name"
                          readOnly
                          grow
                          title="Filial"
                          defaultValue={pageData.filial?.name ?? ""}
                          InputContext={InputContext}
                        />
                      </InputLine>
                      <InputLine title="Student">
                        <Input
                          type="text"
                          name="registration_number"
                          readOnly
                          grow
                          title="Registration Number"
                          defaultValue={student?.registration_number ?? ""}
                          InputContext={InputContext}
                        />
                        <Input
                          type="text"
                          name="student_name"
                          readOnly
                          grow
                          title="Student Name"
                          defaultValue={`${student?.name ?? ""} ${
                            student?.last_name ?? ""
                          }`}
                          InputContext={InputContext}
                        />
                        <Input
                          type="text"
                          name="student_email"
                          readOnly
                          grow
                          title="Email"
                          defaultValue={student?.email ?? ""}
                          InputContext={InputContext}
                        />
                      </InputLine>
                    </InputLineGroup>

                    <InputLineGroup
                      title="INVOICES"
                      activeMenu={activeMenu === "invoices"}
                    >
                      {globalSession === null ? (
                        <InputLine>
                          <p className="text-xs text-gray-400 py-2">
                            No session found for this student.
                          </p>
                        </InputLine>
                      ) : (
                        <>
                          {sessions.length > 1 && (
                            <InputLine title="Period">
                              <div className="w-full flex flex-row flex-wrap gap-2">
                                {sessions.map((session) => {
                                  const isSelected =
                                    session.id === selectedSessionId;
                                  return (
                                    <button
                                      key={session.id}
                                      type="button"
                                      onClick={() =>
                                        setSelectedSessionId(session.id)
                                      }
                                      className={`rounded px-3 py-2 text-xs border flex flex-row items-center gap-2 ${
                                        isSelected
                                          ? "bg-mila_orange text-white border-mila_orange font-semibold"
                                          : "bg-white text-gray-600 border-gray-300 hover:bg-gray-100"
                                      }`}
                                    >
                                      {formatSessionLabel(session)}
                                      {session.is_current && (
                                        <span
                                          className={`rounded px-1 py-0.5 text-[10px] ${
                                            isSelected
                                              ? "bg-white/25"
                                              : "bg-green-100 text-green-700"
                                          }`}
                                        >
                                          Current
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </InputLine>
                          )}
                          <InputLine title="Session">
                            <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                              <div className="flex gap-6">
                                <span>
                                  <span className="text-gray-500">Period:</span>{" "}
                                  <strong>
                                    {displayedSession?.session_start_date} →{" "}
                                    {displayedSession?.session_end_date}
                                  </strong>
                                </span>
                                <span>
                                  <span className="text-gray-500">Payment:</span>{" "}
                                  <strong className="capitalize">
                                    {displayedSession?.payment_type ?? "-"}
                                  </strong>
                                </span>
                                <span>
                                  <span className="text-gray-500">Status:</span>{" "}
                                  <strong className="capitalize">
                                    {displayedSession?.status ?? "-"}
                                  </strong>
                                </span>
                              </div>
                              {canChangePaymentType && (
                                <div className="flex flex-row items-center gap-2">
                                  {editingPaymentType ? (
                                    <>
                                      <select
                                        value={newPaymentType}
                                        onChange={(e) =>
                                          setNewPaymentType(e.target.value)
                                        }
                                        className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                                      >
                                        <option value="full">Full payment</option>
                                        <option value="installments">
                                          3x installments
                                        </option>
                                      </select>
                                      <button
                                        type="button"
                                        disabled={savingPaymentType}
                                        onClick={handleChangePaymentType}
                                        className="rounded px-2 py-1 text-xs bg-mila_orange text-white disabled:opacity-50"
                                      >
                                        <strong>Save</strong>
                                      </button>
                                      <button
                                        type="button"
                                        disabled={savingPaymentType}
                                        onClick={() => setEditingPaymentType(false)}
                                        className="rounded px-2 py-1 text-xs bg-white text-gray-600 border border-gray-300"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={startChangePaymentType}
                                      className="rounded px-2 py-1 text-xs bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
                                    >
                                      Change Payment Type
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </InputLine>
                          <InputLine title="Payment Method">
                            <SelectPopover
                              name="global_paymentmethod_id"
                              title="Payment Method"
                              isSearchable
                              grow
                              options={paymentMethods}
                              onChange={(el) =>
                                setGlobalPaymentMethod(el?.value || null)
                              }
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Invoices">
                            {(displayedSession?.receivables ?? []).length === 0 ? (
                              <p className="text-xs text-gray-400 py-2">
                                No invoices found.
                              </p>
                            ) : (
                              <table className="w-full text-xs border-collapse">
                                <thead>
                                  <tr className="bg-gray-100">
                                    <th className="text-left px-2 py-1">Type</th>
                                    <th className="text-left px-2 py-1">Due Date</th>
                                    <th className="text-right px-2 py-1">Amount</th>
                                    <th className="text-right px-2 py-1">Discount</th>
                                    <th className="text-right px-2 py-1">Total</th>
                                    <th className="text-left px-2 py-1">Status</th>
                                    <th className="text-right px-2 py-1">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayedSession.receivables.map((inv) => (
                                    <tr
                                      key={inv.id}
                                      className="border-b even:bg-gray-50"
                                    >
                                      <td className="px-2 py-1">{inv.type_detail}</td>
                                      <td className="px-2 py-1">
                                        {inv.due_date || "-"}
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        ${parseFloat(inv.amount || 0).toFixed(2)}
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        ${invoiceDiscount(inv).toFixed(2)}
                                      </td>
                                      <td className="px-2 py-1 text-right font-semibold">
                                        ${invoiceTotal(inv).toFixed(2)}
                                      </td>
                                      <td className="px-2 py-1">
                                        <span
                                          className={
                                            inv.status === "Paid"
                                              ? "text-green-600 font-semibold"
                                              : "text-red-500"
                                          }
                                        >
                                          {inv.status}
                                        </span>
                                      </td>
                                      <td className="px-2 py-1">
                                        {inv.status !== "Paid" && (
                                          <div className="flex flex-row gap-1 justify-end">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleGlobalSendInvoice(inv)
                                              }
                                              className="rounded px-2 py-1 flex flex-row items-center gap-1 text-xs bg-slate-300 text-slate-600 border border-slate-400 hover:bg-slate-400 hover:text-white"
                                            >
                                              <Mail size={12} />
                                              <strong>Send</strong>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleGlobalProcessPayment(inv)
                                              }
                                              className="rounded px-2 py-1 flex flex-row items-center gap-1 text-xs bg-green-200 text-green-800 border border-green-400 hover:bg-green-400 hover:text-white"
                                            >
                                              <PlayCircle size={12} />
                                              <strong>Process Payment</strong>
                                            </button>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </InputLine>
                        </>
                      )}
                    </InputLineGroup>

                    <InputLineGroup
                      title="Comments"
                      activeMenu={activeMenu === "comments"}
                    >
                      <Comments enrollment_id={student?.enrollments?.[0]?.id} />
                    </InputLineGroup>

                    <InputLineGroup title="CANCEL GLOBAL FINANCIAL SESSION" activeMenu={activeMenu === "terminate"}>
                      {renewableSession ? (
                        <InputLine>
                          <div className="w-full flex flex-col gap-3 text-xs">
                            <label className="flex flex-col gap-1">Cancellation date
                              <input type="date" value={terminationDate} onChange={(event) => setTerminationDate(event.target.value)} className="border rounded p-2" />
                            </label>
                            <label className="flex flex-col gap-1">Memo
                              <textarea value={terminationMemo} onChange={(event) => setTerminationMemo(event.target.value)} className="border rounded p-2" rows={4} />
                            </label>
                            <div className="flex justify-start">
                              <button type="button" disabled={terminating} onClick={openCancelModal} className="inline-flex items-center justify-center rounded bg-red-600 text-white px-4 py-2 font-normal cursor-pointer hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {terminating ? "Canceling..." : "Review cancellation"}
                              </button>
                            </div>
                          </div>
                        </InputLine>
                      ) : (
                        <InputLine><p className="text-xs text-gray-500">No active session available for financial cancellation.</p></InputLine>
                      )}
                    </InputLineGroup>
                  </>
                ) : (
                  <FormLoading />
                )}
              </InputContext.Provider>
            </Form>

            {pageData.loaded && student.status !== "Inactive" && student.category !== "Ex-student" && (
              <Form
                ref={renewForm}
                onSubmit={handleRenewSubmit}
                className="w-full pb-32"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm: renewForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  <InputLineGroup
                    title="RENEW SESSION"
                    activeMenu={activeMenu === "renew"}
                  >
                    {renewableSession && (
                      <InputLine title="Current Session">
                        <div className="w-full flex flex-col gap-2 text-xs bg-gray-50 border border-gray-200 rounded-md p-3">
                          <div className="flex gap-6">
                            <span>
                              <span className="text-gray-500">Period:</span>{" "}
                              <strong>
                                {renewableSession?.session_start_date} →{" "}
                                {renewableSession?.session_end_date}
                              </strong>
                            </span>
                            <span>
                              <span className="text-gray-500">Schedule:</span>{" "}
                              <strong>{renewableSession?.plan_schedule ?? "-"}</strong>
                            </span>
                            <span>
                              <span className="text-gray-500">Payment:</span>{" "}
                              <strong className="capitalize">
                                {renewableSession?.payment_type ?? "-"}
                              </strong>
                            </span>
                          </div>
                        </div>
                      </InputLine>
                    )}

                    {pendingSession && (
                      <InputLine title="Scheduled Session">
                        <div className="w-full flex flex-row items-center justify-between gap-4 text-xs bg-amber-50 border border-amber-200 rounded-md p-3">
                          <span>
                            There is a scheduled session for{" "}
                            <strong>
                              {pendingSession.session_start_date} →{" "}
                              {pendingSession.session_end_date}
                            </strong>{" "}
                            waiting to be activated.
                          </span>
                          <button
                            type="button"
                            disabled={activating}
                            onClick={() => handleActivateSession(pendingSession)}
                            className="shrink-0 rounded px-3 py-2 flex flex-row items-center gap-1 text-xs bg-green-200 text-green-800 border border-green-400 hover:bg-green-400 hover:text-white disabled:opacity-50"
                          >
                            <PlayCircle size={12} />
                            <strong>
                              {activating ? "Activating..." : "Activate Now"}
                            </strong>
                          </button>
                        </div>
                      </InputLine>
                    )}

                    {(pendingSession || renewableSession) && (
                      <InputLine title={pendingSession ? "Scheduled session autopay" : "Current session autopay"}>
                        <GlobalAutopay key={(pendingSession || renewableSession).id}
                          session={pendingSession || renewableSession} refresh={getPageData} />
                      </InputLine>
                    )}

                    <InputLine title="Billing Mode">
                      <SelectPopover
                        name="pricing_mode_display"
                        grow
                        required
                        title="Billing Mode"
                        options={[
                          { value: "fixed", label: "Fixed price" },
                          { value: "hourly", label: "Per hour" },
                        ]}
                        defaultValue={{ value: "fixed", label: "Fixed price" }}
                        onChange={(el) => setPricingMode(el?.value || "fixed")}
                        InputContext={InputContext}
                      />
                    </InputLine>

                    <InputLine title="New Session">
                      <SessionDateField
                        name="session_start_date"
                        grow
                        required
                        title="Session Start Date"
                        InputContext={InputContext}
                        onChange={setSessionStartDate}
                      />
                      <SessionDateField
                        name="session_end_date"
                        grow
                        required
                        title="Session End Date"
                        InputContext={InputContext}
                        onChange={setSessionEndDate}
                      />
                                  <GlobalTuitionDueDate
                                    Field={SessionDateField}
                                    sessionStartDate={sessionStartDate}
                                    InputContext={InputContext}
                                    onChange={setFirstTuitionDueDate}
                                  />
                    </InputLine>

                    <InputLine>
                      <SelectPopover
                        name="plan_schedule"
                        grow
                        required={pricingMode === "hourly"}
                        title="Plan Schedule"
                        isSearchable
                        options={planScheduleOptions}
                        onChange={(el) => setPlanScheduleId(el?.value || null)}
                        InputContext={InputContext}
                      />
                    </InputLine>

                    {pricingMode === "fixed" && (
                      <InputLine title="Payment">
                        <SelectPopover
                          name="payment_type_display"
                          grow
                          required
                          title="Payment Type"
                          options={[
                            { value: "full", label: "Full payment" },
                            { value: "installments", label: "3x installments" },
                          ]}
                          defaultValue={{ value: "full", label: "Full payment" }}
                          onChange={(el) => setPaymentType(el?.value || "full")}
                          InputContext={InputContext}
                        />
                      </InputLine>
                    )}

                    <InputLine title="Invoice">
                      <SessionDateField
                        name="first_due_date"
                        grow
                        required
                        title="Next Invoice Due Date"
                        InputContext={InputContext}
                        onChange={setFirstDueDate}
                      />
                    </InputLine>

                    <PricesSimulation
                      student={renewSimulationStudent}
                      InputContext={InputContext}
                      FullGridContext={FullGridContext}
                      generalForm={renewForm}
                      showAdmissionDiscounts={true}
                      isAdmissionDiscountChangable={true}
                      showFinancialDiscounts={false}
                      isFinancialDiscountChangable={false}
                      tuitionOnly
                      onTotalsChange={setSimulatedTotals}
                    />

                    {pricingMode === "hourly" && hourlyPreview && (() => {
                      const totalHours = hourlyPreview.total_hours || 0;
                      const ratePerHour = hourlyPreview.rate_per_hour || 0;
                      const tuition = hourlyPreview.tuition || 0;
                      return (
                        <InputLine title="Payment Summary">
                          <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total hours:</span>
                              <span className="font-medium">{totalHours}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Rate per hour:</span>
                              <span className="font-medium">${ratePerHour.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Tuition:</span>
                              <span className="font-medium">${tuition.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-1 font-semibold">
                              <span>Grand Total (paid in full):</span>
                              <span>${tuition.toFixed(2)}</span>
                            </div>
                          </div>
                        </InputLine>
                      );
                    })()}

                    {pricingMode === "fixed" && priceLists && (() => {
                      const tuitionOriginal = priceLists?.tuition || 0;
                      const totalDiscount = simulatedTotals?.totalDiscount || 0;
                      const tuitionAfterDiscount = Math.max(0, tuitionOriginal - totalDiscount);
                      const grandTotal =
                        simulatedTotals?.totalTuition ?? tuitionAfterDiscount;
                      const installmentBase = parseFloat((tuitionAfterDiscount / 3).toFixed(2));
                      const remainder = parseFloat((tuitionAfterDiscount - installmentBase * 3).toFixed(2));
                      return (
                        <InputLine title="Payment Summary">
                          <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Tuition:</span>
                              <span className="font-medium">${tuitionOriginal.toFixed(2)}</span>
                            </div>
                            {totalDiscount > 0 && (
                              <div className="flex justify-between text-green-700">
                                <span>Discount:</span>
                                <span className="font-medium">-${totalDiscount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between border-t pt-1 font-semibold">
                              <span>Grand Total:</span>
                              <span>${grandTotal.toFixed(2)}</span>
                            </div>
                            {paymentType === "installments" && (
                              <div className="mt-1 border-t pt-2 flex flex-col gap-1 text-gray-600">
                                <span className="font-semibold text-gray-700">Installment Preview (Tuition only):</span>
                                <div className="flex justify-between">
                                  <span>1st (tuition due date): {firstTuitionDueDate}</span>
                                  <span>${(installmentBase + remainder).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>2nd (+1 month):</span>
                                  <span>${installmentBase.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>3rd (+2 months):</span>
                                  <span>${installmentBase.toFixed(2)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </InputLine>
                      );
                    })()}

                    <InputLine title="Activation">
                      <SelectPopover
                        name="set_as_current_display"
                        grow
                        required
                        title="When should it take effect?"
                        options={[
                          { value: false, label: "Leave it scheduled" },
                          { value: true, label: "Activate now" },
                        ]}
                        defaultValue={{
                          value: false,
                          label: "Leave it scheduled",
                        }}
                        onChange={(el) => setSetAsCurrent(el?.value === true)}
                        InputContext={InputContext}
                      />
                    </InputLine>

                    <InputLine>
                      <p className="text-xs text-gray-500 py-1">
                        {setAsCurrent
                          ? "The new session starts as active and the current one becomes renewed."
                          : "The new session starts as pending and the current one stays active until you activate it."}
                      </p>
                    </InputLine>

                    <InputLine>
                      <button
                        type="submit"
                        disabled={renewing}
                        className="text-xs bg-mila_orange font-bold text-white px-6 py-3 mt-3 rounded-md border cursor-pointer flex flex-row items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
                      >
                        <CalendarSync size={14} />
                        {renewing ? "Renewing..." : "Renew Session"}
                      </button>
                    </InputLine>
                  </InputLineGroup>
                </InputContext.Provider>
              </Form>
            )}
          </div>
        </div>
      </div>
      <CancelGlobalModal
        open={cancelModalOpen}
        busy={terminating}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleTerminateSession}
        studentName={`${student.name ?? ""} ${student.last_name ?? ""}`.trim()}
        session={renewableSession}
        cancellationDate={terminationDate}
        memo={terminationMemo.trim()}
        futureSessionsCount={sessions.filter((session) => session.status === "pending").length}
      />
    </Preview>
  );
}
